import React, { memo, useMemo, useCallback, useState, useEffect } from 'react'
import { Alert, Col, Row, Form, Checkbox } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../../_utils/object'
import { canShowField, sortKeywordsForDisplay } from '../helpers'
import { KeywordCheckbox } from './keyword-checkbox'
import { KeywordsSection } from './keywords-section'
import { PushlyAiResultsProps, SuggestedFieldsForm } from '../types'
import { VariantCardAlt } from './variant-card-alt'
import { PushlyAi } from '@pushly/aqe/lib/components'
import { VariantCard } from './variant-card'

const MAX_KEYWORDS = 20

/**
 * PushlyAiResults component
 * Handles Pushly AI variant display and selection logic including:
 * - Grid of VariantCardAlt components
 * - Variant selection state management (replace vs add)
 * - Batch variant operations
 */
const PushlyAiResults = memo(
    ({
        domain,
        results,
        builder,
        replaceVariant,
        addVariants,
        onReplaceSelectionChange,
        onAddSelectionChange,
    }: PushlyAiResultsProps) => {
        const [keywordForm] = useForm<SuggestedFieldsForm>()
        const [selectedKeywords, setSelectedKeywords] = useState<string[] | null>(null)

        // Extract keywords from the first variant (assuming all variants have similar keywords)
        const allKeywords = useMemo<string[]>(() => {
            if (!results || results.length === 0) return []
            const firstVariant = results[0]
            const keywords = firstVariant ? extract(firstVariant, 'keywords', []) : []
            return keywords ? keywords.sort().slice(0, MAX_KEYWORDS) : []
        }, [results])

        // Initialize selected keywords
        const hasKwSuggestions = allKeywords.length > 0

        // Initialize selected keywords only once
        useEffect(() => {
            if (hasKwSuggestions && selectedKeywords === null) {
                setSelectedKeywords(allKeywords)
                keywordForm.setFieldsValue({ keywords: true })
            }
        }, [hasKwSuggestions, selectedKeywords, allKeywords, keywordForm])

        // Memoized keyword selection state
        const keywordSelectionState = useMemo(() => {
            const totalSuggestedKws = allKeywords.length
            const totalSelectedKws = selectedKeywords?.length ?? 0
            const allKwSelection = hasKwSuggestions && totalSelectedKws === totalSuggestedKws
            const partialKwSelection = hasKwSuggestions && !allKwSelection && totalSelectedKws > 0

            return {
                totalSuggestedKws,
                totalSelectedKws,
                allKwSelection,
                partialKwSelection,
            }
        }, [hasKwSuggestions, allKeywords.length, selectedKeywords?.length])

        // Update form when keyword selection state changes
        useEffect(() => {
            if (hasKwSuggestions) {
                keywordForm.setFieldsValue({
                    keywords: keywordSelectionState.allKwSelection,
                })
            }
        }, [keywordSelectionState.allKwSelection, hasKwSuggestions, keywordForm])

        // Memoized keyword change handler
        const handleKeywordChange = useCallback(
            (kw: string, checked: boolean) => {
                const currentSelected = selectedKeywords ?? []
                const nextKeywords = checked
                    ? [...currentSelected, kw]
                    : currentSelected.filter((k: string) => k !== kw)
                setSelectedKeywords(nextKeywords)
            },
            [selectedKeywords],
        )

        // Memoized main keyword checkbox handler
        const handleMainKeywordChange = useCallback(
            ({ target: { checked } }: { target: { checked: boolean } }) => {
                const newSelection = checked ? allKeywords : []
                setSelectedKeywords(newSelection)
                keywordForm.setFieldsValue({ keywords: checked })
            },
            [allKeywords],
        )

        // Enhanced selection change handlers that include keywords
        const handleReplaceSelectionChangeWithKeywords = useCallback(
            (variantIndex: number, selected: boolean, selections: any) => {
                // Add keywords to selections if they're selected and field is allowed
                if (selected && selectedKeywords?.length && canShowField(domain, 'keywords')) {
                    selections.keywords = selectedKeywords
                }
                onReplaceSelectionChange(variantIndex, selected, selections)
            },
            [selectedKeywords, domain, onReplaceSelectionChange],
        )

        const handleAddSelectionChangeWithKeywords = useCallback(
            (variantIndex: number, selected: boolean, selections: any) => {
                // Add keywords to selections if they're selected and field is allowed
                if (selected && selectedKeywords?.length && canShowField(domain, 'keywords')) {
                    selections.keywords = selectedKeywords
                }
                onAddSelectionChange(variantIndex, selected, selections)
            },
            [selectedKeywords, domain, onAddSelectionChange],
        )

        const variantCalculations = useMemo(() => {
            const currentVariantCount = builder?.variants?.length ?? 1 // At least 1 variant exists
            const maxVariants = 4
            const selectedAddVariantsCount = Object.values(addVariants).filter((v) => v.selected).length
            const canAddMoreVariants = currentVariantCount + selectedAddVariantsCount < maxVariants
            const hasResults = results && results.length > 0
            const selectedForAddCount = Object.values(addVariants).filter((v) => v.selected).length
            const hasReplaceSelection = replaceVariant !== null
            const hasAddSelections = selectedForAddCount > 0

            return {
                currentVariantCount,
                maxVariants,
                selectedAddVariantsCount,
                canAddMoreVariants,
                hasResults,
                selectedForAddCount,
                hasReplaceSelection,
                hasAddSelections,
            }
        }, [builder?.variants?.length, addVariants, results, replaceVariant])

        if (!variantCalculations.hasResults) {
            return (
                <div className="ai-instructions-section">
                    <div className="instructions-text">
                        <Alert
                            type="warning"
                            showIcon={true}
                            icon={<PushlyAi />}
                            message={
                                <span>
                                    We were unable to generate any notification variants based on the provided Landing
                                    URL. Please try again later or with a different Landing URL.
                                </span>
                            }
                        />
                    </div>
                </div>
            )
        }

        return (
            <>
                <div className="ai-instructions-section">
                    <div className="instructions-text">
                        <PushlyAi /> We have generated the following notification variants based on the provided Landing
                        URL. Please review each variant and select which ones you would like to use by choosing "Replace
                        current" to update your existing notification or "Add as new" to create additional A/B test
                        variants.
                    </div>
                </div>

                <div className="ai-variants-cards">
                    <Row gutter={[16, 16]}>
                        {results.map((variant, index) => {
                            const isSelectedForReplace = replaceVariant?.variantIndex === index
                            const isSelectedForAdd = addVariants[index]?.selected ?? false
                            const hasOtherReplaceSelection =
                                replaceVariant !== null && replaceVariant.variantIndex !== index

                            return (
                                <Col key={index} xs={24} sm={12} lg={12} xl={12}>
                                    <VariantCard
                                        variant={variant}
                                        variantIndex={index}
                                        domain={domain}
                                        canAddVariant={variantCalculations.canAddMoreVariants || isSelectedForAdd}
                                        isSelectedForReplace={isSelectedForReplace}
                                        isSelectedForAdd={isSelectedForAdd}
                                        hasOtherReplaceSelection={hasOtherReplaceSelection}
                                        onReplaceSelectionChange={handleReplaceSelectionChangeWithKeywords}
                                        onAddSelectionChange={handleAddSelectionChangeWithKeywords}
                                    />
                                </Col>
                            )
                        })}
                    </Row>
                </div>

                {hasKwSuggestions && canShowField(domain, 'keywords') && (
                    <KeywordsSection
                        form={keywordForm}
                        keywords={allKeywords}
                        selectedKeywords={selectedKeywords}
                        onKeywordChange={handleKeywordChange}
                        onMainCheckboxChange={handleMainKeywordChange}
                        title="Keywords (applied to all selected variants)"
                        className="shared-keywords-section"
                        allSelected={keywordSelectionState.allKwSelection}
                        partialSelected={keywordSelectionState.partialKwSelection}
                    />
                )}

                {(variantCalculations.hasReplaceSelection || variantCalculations.hasAddSelections) && (
                    <div className="selection-summary">
                        <div className="selection-summary-content">
                            <div className="selection-summary-title">Selection Summary:</div>
                            {variantCalculations.hasReplaceSelection && (
                                <div>
                                    • Replace current variant with Variant {(replaceVariant?.variantIndex ?? 0) + 1}
                                </div>
                            )}
                            {variantCalculations.hasAddSelections && (
                                <div>
                                    • Add {variantCalculations.selectedForAddCount} new variant
                                    {variantCalculations.selectedForAddCount > 1 ? 's' : ''}:
                                    {Object.entries(addVariants)
                                        .filter(([, v]) => v.selected)
                                        .map(([index]) => ` Variant ${parseInt(index, 10) + 1}`)
                                        .join(',')}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        )
    },
)

PushlyAiResults.displayName = 'PushlyAiResults'

export default PushlyAiResults
